import {Autocomplete, Box, FormHelperText, Grid, IconButton, InputAdornment, TextField, Typography} from '@mui/material'
import {Dispatch, SetStateAction} from 'react'
import {Clear} from '@mui/icons-material'
import {useTranslation} from 'react-i18next'
import InputCountry from '../../../../../shared/components/inputs/InputCountry'
import {Slider} from '../../../models/sliders'
import {useGeneralInfoForm} from '../../hooks/useGeneralInfoForm'
import {IStatus} from '@/contexts/backoffice/courses/models/course'
import {useSelector} from 'react-redux'
import {IRootState} from '@/redux/types'
import RoleAutocomplete from '@/contexts/shared/components/inputs/RoleAutocomplete'
import {LanguageMultiAutocomplete, StatusAutocomplete} from '@/contexts/shared/components/inputs'

type GeneralTabProps = {
  type: number
  sliderState: Slider
  setSliderState: Dispatch<SetStateAction<Slider>>
  STATUS: IStatus[]
}

const GeneralInfoForm = ({ type, sliderState, setSliderState, STATUS }: GeneralTabProps) => {
  const { errorForm, msgForm } = useSelector((state: IRootState) => state.validator)

  const { slider_selected } = useSelector((state: IRootState) => state.slider)
  const {t } = useTranslation(['global', 'admin'])

  const { handleNumberChange, handleInputChangeFile, handleInputChange, handleState } = useGeneralInfoForm(
    type,
    sliderState,
    setSliderState
  )

  const handleRemoveImage = () => {
    setSliderState(prev => ({ ...prev, image: null, imagePreview: null }))
  }

  const types = [
    { id: 0, name: t('link') },
    { id: 1, name: t('more-info') }
  ]

  const visibility = [
    { id: 0, name: t('only-users', { ns: 'global' }) },
    { id: 1, name: 'VIP' }
  ]

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={type === 2 ? 3 : 4}>
        <RoleAutocomplete value={sliderState.role} handleInputChange={handleInputChange} />
      </Grid>
      <Grid item xs={12} sm={6} md={type === 2 ? 3 : 4}>
        <InputCountry value={sliderState.countries} handleInputChange={handleState} />
      </Grid>
      {type !== 2 && (
        <Grid item xs={12} sm={6} md={type === 2 ? 3 : 4}>
          <LanguageMultiAutocomplete value={sliderState.languages} handleInputChange={handleInputChange} />
        </Grid>
      )}
      <Grid item xs={12} sm={6} md={type === 2 ? 3 : 4}>
        <StatusAutocomplete value={sliderState.status} handleInputChange={handleInputChange} options={STATUS} />
      </Grid>

      <Grid item xs={12} sm={6} md={type === 2 ? 3 : 4}>
        <Autocomplete
          id="combo-box-visibility"
          disableClearable
          value={visibility.find((vsb: any) => vsb.id === sliderState.visibility)}
          onChange={handleNumberChange('visibility')}
          options={visibility}
          getOptionLabel={(option: any) => option.name}
          renderInput={params => <TextField {...params} label={t('visibility')} variant="filled" InputLabelProps={{ shrink: true }} />}
        />
        <FormHelperText error={errorForm.visibility}>{msgForm.visibility}</FormHelperText>
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <TextField
          label={t('position')}
          type="number"
          fullWidth
          required
          disabled={type !== 1}
          InputProps={{ inputProps: {} }}
          InputLabelProps={{ shrink: true }}
          value={sliderState.order}
          error={errorForm.order}
          helperText={msgForm.order}
          onChange={e => handleInputChange('order', e.target.value)}
          variant="filled"
        />
        <FormHelperText error={errorForm.order}>{msgForm.order}</FormHelperText>
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <Autocomplete
          id="combo-box-types"
          value={types.find((type: any) => type.id === sliderState.type)}
          onChange={handleNumberChange('type')}
          options={types}
          getOptionLabel={(option: any) => (option.id !== undefined ? option.name : '')}
          renderInput={params => <TextField {...params} label={t('action')} variant="filled" InputLabelProps={{ shrink: true }} />}
        />
        <FormHelperText error={errorForm.type}>{msgForm.type}</FormHelperText>
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <TextField
          label={sliderState.type === 0 ? 'URL' : 'Email'}
          fullWidth
          margin="normal"
          value={sliderState.value}
          error={errorForm.value}
          helperText={msgForm.value}
          onChange={e => handleInputChange('value', e.target.value)}
          InputLabelProps={{ shrink: true }}
          variant="filled"
        />
      </Grid>

      <Grid item xs={12} md={type === 2 ? 12 : 4}>
        {sliderState.image ? (
          <TextField
            label={t('image-url')}
            fullWidth
            margin="normal"
            variant="filled"
            value={sliderState.image.name}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleRemoveImage} color="error">
                    <Clear />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        ) : (
          <TextField
            id="txt-file-image"
            label={t('image-url')}
            fullWidth
            margin="normal"
            type="file"
            error={errorForm.image}
            helperText={msgForm.image}
            onChange={handleInputChangeFile}
            InputLabelProps={{ shrink: true }}
            variant="filled"
          />
        )}
        {(sliderState.imagePreview && sliderState.image === null) && (
          <Box
            component="div"
            p={1}
            display="grid"
            justifyContent="center"
            my={2}
            borderRadius={8}
            gap={8}
            style={{ backgroundColor: '#FAFAFA' }}
          >
            <Typography variant="body2">{`${t('admin.library.preview')}:`}</Typography>
            <img src={sliderState.imagePreview} width="100%" height="auto" alt={slider_selected.title} />
          </Box>
        )}
      </Grid>
    </Grid>
  )
}

export default GeneralInfoForm
